import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    LinearProgress,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { apiString as API_URL } from '../../utils/constants';

import AiUsageSummaryCards from './AiUsageSummaryCards';
import AiUsageChart from './AiUsageChart';
import AiUsageAnalyticsPanel from './AiUsageAnalyticsPanel';
import AiRateLimitPanel, { AiRateLimitConfigDto, ProviderPreview, ProviderRole } from './AiRateLimitPanel';
import AiPromptsPanel from './AiPromptsPanel';

// Local date helpers to construct YYYY-MM-DD
const getLocalDateString = (date: Date) => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().split('T')[0];
};

export interface AiUsageByDayDto {
    Date: string;
    Requests: number;
    PromptTokens: number;
    CompletionTokens: number;
    TotalTokens: number;
    CostUsd?: number;
}

export interface AiUsageByProviderDto {
    ProviderId: string;
    ProviderName: string;
    RoleName: string;
    Requests: number;
    TotalTokens: number;
    CostUsd?: number;
}

export interface AiUsageByModelDto {
    ModelDeployment: string;
    Requests: number;
    TotalTokens: number;
    CostUsd?: number;
}

export interface AiUsageByFeatureDto {
    Feature: string;
    Requests: number;
    TotalTokens: number;
    CostUsd?: number;
}

export interface AiUsageSummaryDto {
    TotalRequests: number;
    TotalPromptTokens: number;
    TotalCompletionTokens: number;
    TotalTokens: number;
    AverageDurationMs: number;
    TotalCostUsd?: number;
    ProjectedMonthlyCostUsd?: number;
    CurrentMonthProjectedCostUsd?: number;
    DailyBreakdown: AiUsageByDayDto[];
    TopProviders: AiUsageByProviderDto[];
    ByModel: AiUsageByModelDto[];
    ByFeature?: AiUsageByFeatureDto[];
}

interface AiManagementPageProps {
    title: string;
}

const AiManagementPage: React.FC<AiManagementPageProps> = ({ title }) => {
    // Current Active Tab
    const [tab, setTab] = useState(0);

    // Global toggle status
    const [enabled, setEnabled] = useState<boolean>(true);
    const [globalLoading, setGlobalLoading] = useState<boolean>(true);
    const [savingGlobal, setSavingGlobal] = useState<boolean>(false);

    // Member and Team Summary toggle status
    const [memberSummaryEnabled, setMemberSummaryEnabled] = useState<boolean>(true);
    const [teamSummaryEnabled, setTeamSummaryEnabled] = useState<boolean>(true);
    const [savingMemberSummary, setSavingMemberSummary] = useState<boolean>(false);
    const [savingTeamSummary, setSavingTeamSummary] = useState<boolean>(false);

    // Data for Overview tab (30-day default)
    const [overviewSummary, setOverviewSummary] = useState<AiUsageSummaryDto | null>(null);
    const [overviewLoading, setOverviewLoading] = useState<boolean>(true);

    // Data for Analytics tab
    const [analyticsFromDate, setAnalyticsFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return getLocalDateString(d);
    });
    const [analyticsToDate, setAnalyticsToDate] = useState(() => getLocalDateString(new Date()));
    const [analyticsSummary, setAnalyticsSummary] = useState<AiUsageSummaryDto | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState<boolean>(false);

    // Data for Configurations tab
    const [configs, setConfigs] = useState<AiRateLimitConfigDto[]>([]);
    const [roles, setRoles] = useState<ProviderRole[]>([]);
    const [providers, setProviders] = useState<ProviderPreview[]>([]);
    const [providerMonthlyUsage, setProviderMonthlyUsage] = useState<{ ProviderId: string; TotalTokens: number }[]>([]);
    const [configLoading, setConfigLoading] = useState<boolean>(false);

    // AI Guardrail Taxonomy State
    const [allowedInstructions, setAllowedInstructions] = useState<string>('');
    const [forbiddenInstructions, setForbiddenInstructions] = useState<string>('');
    const [guardrailLoading, setGuardrailLoading] = useState<boolean>(false);
    const [savingGuardrail, setSavingGuardrail] = useState<boolean>(false);

    // AI Member Summary State
    const [refreshDays, setRefreshDays] = useState<number>(7);
    const [savingRefreshDays, setSavingRefreshDays] = useState<boolean>(false);

    // Active Member Indexing State
    const [activeProgress, setActiveProgress] = useState<{
        total: number;
        processed: number;
        stage: string;
        isComplete: boolean;
        hasError: boolean;
        isRunning: boolean;
        isCancelled: boolean;
    } | null>(null);
    const [isCancellingActive, setIsCancellingActive] = useState<boolean>(false);
    const [confirmActiveIndexingModal, setConfirmActiveIndexingModal] = useState<boolean>(false);
    // AI Team Summary State
    const [teamRefreshDays, setTeamRefreshDays] = useState<number>(7);
    const [savingTeamRefreshDays, setSavingTeamRefreshDays] = useState<boolean>(false);

    // Toast/Snackbar notifications
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const fetchActiveMembersProgress = async () => {
        try {
            const res = await axios.get(`${API_URL}/AiIndexing/active-members-progress`);
            if (res.data) {
                const data = {
                    total: res.data.total ?? res.data.Total ?? 0,
                    processed: res.data.processed ?? res.data.Processed ?? 0,
                    stage: res.data.stage ?? res.data.Stage ?? 'Idle',
                    isComplete: res.data.isComplete ?? res.data.IsComplete ?? false,
                    hasError: res.data.hasError ?? res.data.HasError ?? false,
                    isRunning: res.data.isRunning ?? res.data.IsRunning ?? false,
                    isCancelled: res.data.isCancelled ?? res.data.IsCancelled ?? false,
                };
                setActiveProgress((prev) => {
                    if (prev?.isRunning && data.stage === 'Not started' && !data.isComplete) {
                        return prev;
                    }
                    return data;
                });
            }
        } catch (err) {
            console.error('Failed to fetch active members progress:', err);
        }
    };

    useEffect(() => {
        document.title = title;
        fetchAiStatus();
        fetchOverviewData();
        fetchAiGuardrailSettings();
        fetchActiveMembersProgress();
        const interval = setInterval(() => {
            fetchActiveMembersProgress();
        }, 1500);
        return () => clearInterval(interval);
    }, [title]);

    const handleTriggerActiveIndexing = async () => {
        setConfirmActiveIndexingModal(false);
        setActiveProgress({
            total: 0,
            processed: 0,
            stage: 'Querying active members...',
            isRunning: true,
            isComplete: false,
            hasError: false,
            isCancelled: false,
        });
        try {
            const res = await axios.post(`${API_URL}/AiIndexing/index-active-members`);
            const msg = res.data?.message || res.data?.Message || (typeof res.data === 'string' ? res.data : 'Active member indexing started');
            setToast({ open: true, message: msg, severity: 'success' });
            setTimeout(fetchActiveMembersProgress, 500);
        } catch (err: any) {
            setActiveProgress(null);
            const errMsg = err.response?.data?.message || err.response?.data?.Message || (typeof err.response?.data === 'string' ? err.response.data : 'Failed to start active member indexing');
            setToast({ open: true, message: errMsg, severity: 'error' });
        }
    };

    const handleCancelActiveIndexing = async () => {
        try {
            setIsCancellingActive(true);
            const res = await axios.post(`${API_URL}/AiIndexing/cancel-active-members`);
            const msg = res.data?.message || res.data?.Message || (typeof res.data === 'string' ? res.data : 'Indexing cancellation requested');
            setToast({ open: true, message: msg, severity: 'success' });
            await fetchActiveMembersProgress();
        } catch (err: any) {
            setToast({ open: true, message: 'Failed to cancel indexing', severity: 'error' });
        } finally {
            setIsCancellingActive(false);
        }
    };

    // Handle tab change and refresh active tab data
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
        if (newValue === 0) {
            fetchOverviewData();
        } else if (newValue === 1) {
            fetchAnalyticsData(analyticsFromDate, analyticsToDate);
        } else if (newValue === 2) {
            fetchConfigData();
        } else if (newValue === 4) {
            fetchAiStatus();
            fetchAiGuardrailSettings();
        }
    };

    const handleCloseToast = () => {
        setToast((prev) => ({ ...prev, open: false }));
    };

    // --- API Calls ---

    // 1. Get enabled status
    const fetchAiStatus = async () => {
        try {
            setGlobalLoading(true);
            const [globalRes, memberRes, teamRes] = await Promise.all([
                axios.get<boolean>(`${API_URL}/chpai/enabled`),
                axios.get<boolean>(`${API_URL}/chpai/member-summary/enabled-status`),
                axios.get<boolean>(`${API_URL}/chpai/team-summary/enabled-status`),
            ]);
            setEnabled(globalRes.data);
            setMemberSummaryEnabled(memberRes.data);
            setTeamSummaryEnabled(teamRes.data);
        } catch (error: any) {
            setToast({
                open: true,
                message: 'Failed to fetch global AI status.',
                severity: 'error',
            });
        } finally {
            setGlobalLoading(false);
        }
    };

    // 2. Toggle global enabled status
    const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        const nextEnabled = !nextValue;
        try {
            setSavingGlobal(true);
            await axios.post(`${API_URL}/chpai/toggle`, { enabled: nextEnabled });
            setEnabled(nextEnabled);
            setToast({
                open: true,
                message: nextEnabled
                    ? 'All CHP AI Services enabled successfully.'
                    : 'All CHP AI Services disabled successfully.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to update AI status.',
                severity: 'error',
            });
        } finally {
            setSavingGlobal(false);
        }
    };

    // Toggle Member Summary enabled status
    const handleToggleMemberSummary = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        try {
            setSavingMemberSummary(true);
            await axios.post(`${API_URL}/chpai/member-summary/toggle`, { enabled: nextValue });
            setMemberSummaryEnabled(nextValue);
            setToast({
                open: true,
                message: nextValue
                    ? 'AI Member Summary enabled.'
                    : 'AI Member Summary disabled.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to update Member Summary status.',
                severity: 'error',
            });
        } finally {
            setSavingMemberSummary(false);
        }
    };

    // Toggle Team Summary enabled status
    const handleToggleTeamSummary = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        try {
            setSavingTeamSummary(true);
            await axios.post(`${API_URL}/chpai/team-summary/toggle`, { enabled: nextValue });
            setTeamSummaryEnabled(nextValue);
            setToast({
                open: true,
                message: nextValue
                    ? 'AI Team Summary enabled.'
                    : 'AI Team Summary disabled.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to update Team Summary status.',
                severity: 'error',
            });
        } finally {
            setSavingTeamSummary(false);
        }
    };

    // 3. Fetch overview summary (fixed last 30 days)
    const fetchOverviewData = async () => {
        try {
            setOverviewLoading(true);
            const to = getLocalDateString(new Date());
            const from = new Date();
            from.setDate(from.getDate() - 30);
            const fromStr = getLocalDateString(from);

            const response = await axios.get<AiUsageSummaryDto>(
                `${API_URL}/chpai/usage/summary?from=${fromStr}&to=${to}`
            );
            setOverviewSummary(response.data);
        } catch (error: any) {
            setToast({
                open: true,
                message: 'Failed to load overview usage statistics.',
                severity: 'error',
            });
        } finally {
            setOverviewLoading(false);
        }
    };

    // 3b. Fetch AI Guardrail settings
    const fetchAiGuardrailSettings = async () => {
        try {
            setGuardrailLoading(true);
            const response = await axios.get(`${API_URL}/setting/getsetting`);
            if (response.data) {
                if (response.data.AiGuardrailSetting) {
                    setAllowedInstructions(response.data.AiGuardrailSetting.AllowedInstructions || '');
                    setForbiddenInstructions(response.data.AiGuardrailSetting.ForbiddenInstructions || '');
                }
                setRefreshDays(response.data.AiSummaryRefreshDays ?? response.data.aiSummaryRefreshDays ?? 7);
                setTeamRefreshDays(response.data.TeamSummaryRefreshDays ?? response.data.teamSummaryRefreshDays ?? 7);
            }
        } catch (error: any) {
            setToast({
                open: true,
                message: 'Failed to fetch AI settings.',
                severity: 'error',
            });
        } finally {
            setGuardrailLoading(false);
        }
    };

    // 3c. Save AI Guardrail settings
    const handleSaveGuardrail = async () => {
        try {
            setSavingGuardrail(true);
            await axios.put(`${API_URL}/setting/updateaiguardrailsetting`, {
                AllowedInstructions: allowedInstructions,
                ForbiddenInstructions: forbiddenInstructions,
            });
            setToast({
                open: true,
                message: 'AI Guardrail settings successfully saved.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to save AI Guardrail settings.',
                severity: 'error',
            });
        } finally {
            setSavingGuardrail(false);
        }
    };

    // 3d. Save AI Member Summary refresh days
    const handleSaveRefreshDays = async () => {
        try {
            setSavingRefreshDays(true);
            await axios.put(`${API_URL}/setting/updateaisummaryrefreshdays?days=${refreshDays}`);
            setToast({
                open: true,
                message: 'AI Member Summary refresh rate saved.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to save refresh rate.',
                severity: 'error',
            });
        } finally {
            setSavingRefreshDays(false);
        }
    };

    // Save AI Team Summary refresh days
    const handleSaveTeamRefreshDays = async () => {
        try {
            setSavingTeamRefreshDays(true);
            await axios.put(`${API_URL}/setting/updateteamsummaryrefreshdays?days=${teamRefreshDays}`);
            setToast({
                open: true,
                message: 'AI Team Summary refresh rate saved.',
                severity: 'success',
            });
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to save team refresh rate.',
                severity: 'error',
            });
        } finally {
            setSavingTeamRefreshDays(false);
        }
    };

    // 4. Fetch filtered usage summary (Analytics Tab)
    const fetchAnalyticsData = async (from: string, to: string) => {
        try {
            setAnalyticsLoading(true);
            const response = await axios.get<AiUsageSummaryDto>(
                `${API_URL}/chpai/usage/summary?from=${from}&to=${to}`
            );
            setAnalyticsSummary(response.data);
        } catch (error: any) {
            setToast({
                open: true,
                message: 'Failed to load filtered usage summary.',
                severity: 'error',
            });
        } finally {
            setAnalyticsLoading(false);
        }
    };

    // 5. Fetch configs, roles, providers, and current month usage (Config Tab)
    const fetchConfigData = async () => {
        try {
            setConfigLoading(true);
            
            // Current calendar month dates
            const now = new Date();
            const startOfMonth = getLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1));
            const today = getLocalDateString(now);

            const [configsRes, rolesRes, providersRes, usageRes] = await Promise.all([
                axios.get<AiRateLimitConfigDto[]>(`${API_URL}/chpai/ratelimit/configs`),
                axios.get<ProviderRole[]>(`${API_URL}/provider/getroles`),
                axios.get<ProviderPreview[]>(`${API_URL}/provider/getproviderpreview`),
                axios.get<AiUsageSummaryDto>(`${API_URL}/chpai/usage/summary?from=${startOfMonth}&to=${today}`),
            ]);

            setConfigs(configsRes.data);
            setRoles(rolesRes.data);
            
            // Only list active providers
            const activeProviders = (providersRes.data || []).filter(p => p.IsActive);
            setProviders(activeProviders);

            // Extract provider usage this month
            const usageList = (usageRes.data?.TopProviders || []).map(p => ({
                ProviderId: p.ProviderId,
                TotalTokens: p.TotalTokens
            }));
            setProviderMonthlyUsage(usageList);

        } catch (error: any) {
            setToast({
                open: true,
                message: 'Failed to load rate limits and provider configuration data.',
                severity: 'error',
            });
        } finally {
            setConfigLoading(false);
        }
    };

    const handleAnalyticsDateChange = (from: string, to: string) => {
        setAnalyticsFromDate(from);
        setAnalyticsToDate(to);
        fetchAnalyticsData(from, to);
    };

    return (
        <Box p={4} maxWidth={1400} mx="auto">
            {/* Toast Notifications */}
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={toast.open}
                autoHideDuration={4000}
                onClose={handleCloseToast}
            >
                <Alert
                    sx={{ width: '100%' }}
                    variant="filled"
                    elevation={6}
                    severity={toast.severity}
                    onClose={handleCloseToast}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Header */}
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4" fontWeight="700" color="textPrimary" gutterBottom>
                        AI Management & Governance
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        Monitor AI usage, trace prompt tokens, configure user rate limits, and set monthly budgets
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                        if (tab === 0) fetchOverviewData();
                        else if (tab === 1) fetchAnalyticsData(analyticsFromDate, analyticsToDate);
                        else if (tab === 2) fetchConfigData();
                        else if (tab === 3) {
                            fetchAiStatus();
                            fetchAiGuardrailSettings();
                        }
                    }}
                    disabled={overviewLoading || analyticsLoading || configLoading || globalLoading || guardrailLoading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                <Tabs value={tab} onChange={handleTabChange} aria-label="AI management tabs">
                    <Tab label="Overview" id="ai-tab-0" />
                    <Tab label="Usage Analytics" id="ai-tab-1" />
                    <Tab label="Rate Limits & Budgets" id="ai-tab-2" />
                    <Tab label="System Prompts" id="ai-tab-3" />
                    <Tab label="Settings & Guardrails" id="ai-tab-4" />
                </Tabs>
            </Box>

            {/* Tab Panels */}
            {tab === 0 && (
                <Grid container spacing={4}>
                    {/* Quick 30-Day Stats overview */}
                    <Grid size={12}>
                        <Box mb={2}>
                            <Typography variant="h6" fontWeight="600" color="textPrimary">
                                Overview (Last 30 Days)
                            </Typography>
                        </Box>
                        <AiUsageSummaryCards
                            totalRequests={overviewSummary?.TotalRequests || 0}
                            totalTokens={overviewSummary?.TotalTokens || 0}
                            promptTokens={overviewSummary?.TotalPromptTokens || 0}
                            completionTokens={overviewSummary?.TotalCompletionTokens || 0}
                            averageDurationMs={overviewSummary?.AverageDurationMs || 0}
                            activeProviders={overviewSummary?.TopProviders?.length || 0}
                            totalCostUsd={overviewSummary?.TotalCostUsd || 0}
                            projectedCostUsd={overviewSummary?.ProjectedMonthlyCostUsd || 0}
                            currentMonthProjectedCostUsd={overviewSummary?.CurrentMonthProjectedCostUsd || 0}
                            loading={overviewLoading}
                        />
                    </Grid>

                    {/* Chart preview */}
                    {!overviewLoading && overviewSummary && (
                        <Grid size={12}>
                            <AiUsageChart
                                dailyData={overviewSummary.DailyBreakdown}
                                modelData={overviewSummary.ByModel}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {tab === 1 && (
                <AiUsageAnalyticsPanel
                    summary={analyticsSummary}
                    loading={analyticsLoading}
                    fromDate={analyticsFromDate}
                    toDate={analyticsToDate}
                    onDateChange={handleAnalyticsDateChange}
                />
            )}

            {tab === 2 && (
                configLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <AiRateLimitPanel
                        configs={configs}
                        roles={roles}
                        providers={providers}
                        providerMonthlyUsage={providerMonthlyUsage}
                        onRefresh={fetchConfigData}
                        setToast={setToast}
                    />
                )
            )}

            {tab === 3 && (
                <AiPromptsPanel setToast={setToast} />
            )}

            {tab === 4 && (
                <Grid container spacing={4}>
                    {/* Global Enable Switch */}
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Divider sx={{ mb: 2 }}>
                                <Chip label="Global Settings" sx={{ fontWeight: '600' }} />
                            </Divider>

                            {globalLoading ? (
                                <Stack alignItems="center" py={2}>
                                    <CircularProgress size={28} />
                                </Stack>
                            ) : (
                                <Stack spacing={1.5}>
                                    <Typography variant="body2" color="textSecondary">
                                        Control the global availability of the CHP Chart Assistant. Disabling it will prevent all providers from using the assistant or accessing the chat pane.
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!enabled}
                                                onChange={handleToggle}
                                                disabled={savingGlobal}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="subtitle1" fontWeight="600">
                                                Disable All CHP AI Services
                                            </Typography>
                                        }
                                    />
                                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2, mt: 1.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={memberSummaryEnabled}
                                                    onChange={handleToggleMemberSummary}
                                                    disabled={!enabled || savingMemberSummary}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="600" sx={{ opacity: enabled ? 1 : 0.5 }}>
                                                        Enable Member Summary
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" sx={{ opacity: enabled ? 1 : 0.5 }}>
                                                        Allow providers to see and generate AI Member Summaries
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={teamSummaryEnabled}
                                                    onChange={handleToggleTeamSummary}
                                                    disabled={!enabled || savingTeamSummary}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="600" sx={{ opacity: enabled ? 1 : 0.5 }}>
                                                        Enable Team Summary
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" sx={{ opacity: enabled ? 1 : 0.5 }}>
                                                        Allow providers to see and generate AI Team Summaries
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    {/* AI Member Summary Settings */}
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Divider sx={{ mb: 2 }}>
                                <Chip label="AI Member Summary Refresh Setting" sx={{ fontWeight: '600' }} />
                            </Divider>

                            {guardrailLoading ? (
                                <Stack alignItems="center" py={2}>
                                    <CircularProgress size={28} />
                                </Stack>
                            ) : (
                                <Stack spacing={3}>
                                    <Typography variant="body2" color="textSecondary">
                                        Configure how often the AI-generated Member Summary should be refreshed. If a summary was generated more than the specified number of days ago, it will be automatically regenerated when a user opens the member's profile.
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                        <TextField
                                            type="number"
                                            label="Refresh Rate (Days)"
                                            value={refreshDays}
                                            disabled={!memberSummaryEnabled || !enabled}
                                            onChange={(e) => setRefreshDays(Math.max(0, parseInt(e.target.value) || 0))}
                                            sx={{ width: '220px' }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={savingRefreshDays || !memberSummaryEnabled || !enabled}
                                            onClick={handleSaveRefreshDays}
                                            sx={{ minWidth: '120px', borderRadius: 2 }}
                                        >
                                            {savingRefreshDays ? <CircularProgress size={24} color="inherit" /> : 'Save Refresh Rate'}
                                        </Button>
                                    </Box>
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    {/* AI Team Summary Refresh Setting */}
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Divider sx={{ mb: 2 }}>
                                <Chip label="AI Team Summary Refresh Setting" sx={{ fontWeight: '600' }} />
                            </Divider>

                            {guardrailLoading ? (
                                <Stack alignItems="center" py={2}>
                                    <CircularProgress size={28} />
                                </Stack>
                            ) : (
                                <Stack spacing={3}>
                                    <Typography variant="body2" color="textSecondary" sx={{ opacity: (teamSummaryEnabled && enabled) ? 1 : 0.5 }}>
                                        Configure how often the AI-generated Team Summary should be refreshed. If a summary was generated more than the specified number of days ago, it will be automatically regenerated when a user opens the care team's profile.
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                        <TextField
                                            type="number"
                                            label="Refresh Rate (Days)"
                                            value={teamRefreshDays}
                                            disabled={!teamSummaryEnabled || !enabled}
                                            onChange={(e) => setTeamRefreshDays(Math.max(0, parseInt(e.target.value) || 0))}
                                            sx={{ width: '220px' }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={savingTeamRefreshDays || !teamSummaryEnabled || !enabled}
                                            onClick={handleSaveTeamRefreshDays}
                                            sx={{ minWidth: '120px', borderRadius: 2 }}
                                        >
                                            {savingTeamRefreshDays ? <CircularProgress size={24} color="inherit" /> : 'Save Refresh Rate'}
                                        </Button>
                                    </Box>
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    {/* AI Guardrails Taxonomy */}
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Divider sx={{ mb: 2 }}>
                                <Chip label="AI Guardrails Taxonomy" sx={{ fontWeight: '600' }} />
                            </Divider>

                            {guardrailLoading ? (
                                <Stack alignItems="center" py={4}>
                                    <CircularProgress size={28} />
                                </Stack>
                            ) : (
                                <Stack spacing={3}>
                                    <Typography variant="body2" color="textSecondary">
                                        Define the Allowed and Forbidden topics for the AI globally. Use bullet points or specific guidelines.
                                    </Typography>
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Allowed context includes member clinical data, behavioral health context, care coordination workflows, and general clinical knowledge. Forbidden topics include prescribing/dosing decisions, billing/legal advice, non-clinical small talk, and coding help.
                                    </Alert>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={6}
                                                    label="Allowed Instructions"
                                                    placeholder="e.g. - General clinical knowledge tied to the current member's context..."
                                                    value={allowedInstructions}
                                                    onChange={(e) => setAllowedInstructions(e.target.value)}
                                                    variant="outlined"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={6}
                                                    label="Forbidden Instructions"
                                                    placeholder="e.g. - Non-clinical small talk or jokes..."
                                                    value={forbiddenInstructions}
                                                    onChange={(e) => setForbiddenInstructions(e.target.value)}
                                                    variant="outlined"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Stack direction="row" justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={savingGuardrail}
                                            onClick={handleSaveGuardrail}
                                            sx={{ minWidth: '120px', borderRadius: 2 }}
                                        >
                                            {savingGuardrail ? <CircularProgress size={24} color="inherit" /> : 'Save Guardrails'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    {/* Active Member AI Indexing Task */}
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Divider sx={{ mb: 2 }}>
                                <Chip label="Active Member AI Indexing Task" sx={{ fontWeight: '600' }} />
                            </Divider>

                            <Stack spacing={2}>
                                <Typography variant="body2" color="textSecondary">
                                    Trigger a full background indexing task for all currently active members in Azure AI Search. This process includes rate limiting, session persistence across refreshes, and progress tracking.
                                </Typography>

                                <Box>
                                    <Button
                                        disabled={activeProgress?.isRunning ?? false}
                                        variant="contained"
                                        color="primary"
                                        startIcon={<StorageIcon />}
                                        onClick={() => setConfirmActiveIndexingModal(true)}
                                    >
                                        Index All Active Members
                                    </Button>
                                </Box>

                                {activeProgress && activeProgress.stage !== 'Not started' && (
                                    <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                Active Member Indexing Progress
                                            </Typography>
                                            {activeProgress.isRunning && (
                                                <Chip label="RUNNING" color="primary" size="small" />
                                            )}
                                            {activeProgress.isComplete && !activeProgress.isCancelled && (
                                                <Chip label="COMPLETE" color="success" size="small" />
                                            )}
                                            {activeProgress.isCancelled && (
                                                <Chip label="CANCELLED" color="warning" size="small" />
                                            )}
                                        </Stack>

                                        {activeProgress.isRunning && (activeProgress.total === 0 || activeProgress.processed === 0) ? (
                                            <LinearProgress variant="indeterminate" sx={{ height: 10, borderRadius: 5, mb: 1 }} />
                                        ) : (
                                            <LinearProgress
                                                variant="determinate"
                                                value={activeProgress.total > 0 ? Math.min(100, Math.round((activeProgress.processed / activeProgress.total) * 100)) : 0}
                                                sx={{ height: 10, borderRadius: 5, mb: 1 }}
                                            />
                                        )}

                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            {activeProgress.processed} / {activeProgress.total} documents ({activeProgress.total > 0 ? Math.round((activeProgress.processed / activeProgress.total) * 100) : 0}%) — <em>{activeProgress.stage}</em>
                                        </Typography>

                                        {activeProgress.isRunning && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<CancelIcon />}
                                                onClick={handleCancelActiveIndexing}
                                                disabled={isCancellingActive}
                                                sx={{ mt: 1 }}
                                            >
                                                {isCancellingActive ? 'Cancelling...' : 'Cancel Indexing'}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Confirmation Dialog for Active Member Indexing */}
            <Dialog
                open={confirmActiveIndexingModal}
                onClose={() => setConfirmActiveIndexingModal(false)}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Confirm Full Active Member AI Indexing
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to trigger AI indexing for <strong>ALL active members</strong>?
                        <br /><br />
                        This job will generate vector embeddings and index all clinical records, documents, notes, encounters, care plans, needs, tasks, and claims for every active member.
                        <br /><br />
                        You will be able to view live progress and cancel execution at any time.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmActiveIndexingModal(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleTriggerActiveIndexing} color="primary" variant="contained" autoFocus>
                        Confirm & Start Indexing
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AiManagementPage;
